import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Edit, Trash2, Loader2, Image as ImageIcon } from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet } from '../lib/api'

interface BannerRow {
  id: string
  title: string
  subtitle: string
  image: string
  linkUrl: string
  position: 'hero' | 'top' | 'side' | 'bottom'
  sortOrder: number
  isActive: boolean
  validFrom: string | null
  validUntil: string | null
  updatedAt: string | null
}

interface BannersData {
  total: number
  data: BannerRow[]
}

const POSITIONS = ['hero', 'top', 'side', 'bottom'] as const
type Position = (typeof POSITIONS)[number]

const POSITION_LABEL: Record<Position, string> = {
  hero: 'Hero',
  top: 'Superior',
  side: 'Lateral',
  bottom: 'Inferior',
}

function isVigente(b: BannerRow): boolean {
  const now = Date.now()
  if (b.validFrom && new Date(b.validFrom).getTime() > now) return false
  if (b.validUntil && new Date(b.validUntil).getTime() < now) return false
  return true
}

export function Banners() {
  const [position, setPosition] = useState<'' | Position>('')
  const qc = useQueryClient()

  const { data, isLoading, isFetching } = useQuery<BannersData>({
    queryKey: ['admin-banners', position],
    queryFn: () =>
      apiGet(`/api/admin/banners${position ? `?position=${position}` : ''}`),
  })

  const del = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/admin/banners/${id}`, { method: 'DELETE' })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      return j
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-banners'] }),
  })

  function onDelete(b: BannerRow) {
    if (!confirm(`¿Eliminar el banner "${b.title || b.id}"?`)) return
    del.mutate(b.id)
  }

  return (
    <>
      <Topbar
        title="Banners"
        subtitle={data ? `${data.total} banners en total` : 'Cargando…'}
        actions={
          <Link
            to="/banners/nuevo"
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            <Plus className="size-3.5" /> Nuevo banner
          </Link>
        }
      />
      <div className="p-6">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setPosition('')}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              position === ''
                ? 'border-brand-500 bg-brand-50 text-brand-700'
                : 'border-ink-200 bg-white text-ink-700 hover:bg-ink-50'
            }`}
          >
            Todas
          </button>
          {POSITIONS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPosition(p)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                position === p
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-ink-200 bg-white text-ink-700 hover:bg-ink-50'
              }`}
            >
              {POSITION_LABEL[p]}
            </button>
          ))}
        </div>

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
            </div>
          )}

          {data && data.data.length === 0 && (
            <div className="grid place-items-center py-16 text-center">
              <ImageIcon className="size-10 text-ink-300" />
              <p className="mt-3 text-sm font-medium text-ink-700">No hay banners</p>
              <p className="text-xs text-ink-500">Crea el primero para mostrarlo en la home.</p>
            </div>
          )}

          {data && data.data.length > 0 && (
            <div className="relative overflow-x-auto">
              {isFetching && (
                <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                  <Loader2 className="size-4 animate-spin text-brand-600" />
                </div>
              )}
              <table className="w-full text-sm">
                <thead className="bg-ink-50">
                  <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                    <th className="px-4 py-3 font-medium">Banner</th>
                    <th className="px-4 py-3 font-medium">Posición</th>
                    <th className="px-4 py-3 font-medium">Orden</th>
                    <th className="px-4 py-3 font-medium">Vigencia</th>
                    <th className="px-4 py-3 font-medium">Estado</th>
                    <th className="w-24 px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((b) => {
                    const vigente = isVigente(b)
                    return (
                      <tr
                        key={b.id}
                        className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-lg bg-ink-100">
                              {b.image ? (
                                <img
                                  src={b.image}
                                  alt=""
                                  className="size-12 object-cover"
                                  onError={(e) => {
                                    ;(e.currentTarget as HTMLImageElement).style.display = 'none'
                                  }}
                                />
                              ) : (
                                <ImageIcon className="size-5 text-ink-400" />
                              )}
                            </div>
                            <div>
                              <div className="font-semibold text-ink-900">
                                {b.title || <span className="text-ink-400">(sin título)</span>}
                              </div>
                              <div className="line-clamp-1 text-xs text-ink-500">
                                {b.subtitle || b.linkUrl || '—'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="info">{POSITION_LABEL[b.position]}</Badge>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-ink-700">{b.sortOrder}</td>
                        <td className="px-4 py-3 text-xs text-ink-700">
                          {b.validFrom || b.validUntil ? (
                            <>
                              {b.validFrom ? new Date(b.validFrom).toLocaleDateString('es-MX') : '—'}
                              {' → '}
                              {b.validUntil ? new Date(b.validUntil).toLocaleDateString('es-MX') : '—'}
                            </>
                          ) : (
                            <span className="text-ink-400">Siempre</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {!b.isActive ? (
                            <Badge variant="default">Inactivo</Badge>
                          ) : vigente ? (
                            <Badge variant="success">Activo</Badge>
                          ) : (
                            <Badge variant="warning">Fuera de fecha</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <Link
                              to="/banners/$id"
                              params={{ id: b.id }}
                              className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                              title="Editar"
                            >
                              <Edit className="size-4" />
                            </Link>
                            <button
                              type="button"
                              onClick={() => onDelete(b)}
                              disabled={del.isPending}
                              className="rounded-md p-1.5 text-ink-500 hover:bg-red-100 hover:text-red-700 disabled:opacity-50"
                              title="Eliminar"
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Banners
